import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  useColorModeValue,
  Flex,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { MdCalculate, MdRefresh } from 'react-icons/md';
import { taxaSelicService } from '../../../services/authService';

const Calculator = () => {
  const [taxasData, setTaxasData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form inputs
  const [anoInicial, setAnoInicial] = useState('');
  const [anoFinal, setAnoFinal] = useState(new Date().getFullYear().toString());
  const [valorPrecatorio, setValorPrecatorio] = useState('');
  const [irrfPercent, setIrrfPercent] = useState('27.5');

  // Results
  const [results, setResults] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [calculationError, setCalculationError] = useState(null);

  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchTaxas();
  }, []);

  const fetchTaxas = async () => {
    try {
      setLoading(true);
      const response = await taxaSelicService.getAll();
      if (response.success) {
        // Sort by year ascending
        const sortedTaxas = (response.data || []).sort((a, b) => a.ano - b.ano);
        setTaxasData(sortedTaxas);
      }
    } catch (error) {
      console.error('Error fetching taxas:', error);
      setError('Erro ao carregar taxas');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    return (value * 100).toFixed(2).replace('.', ',') + '%';
  };

  const parseCurrencyInput = (value) => {
    // Remove R$, dots (thousands), and replace comma with dot (decimal)
    const cleaned = value.replace(/[R$\s.]/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const formatCurrencyInput = (value) => {
    if (!value) return '';
    const number = parseCurrencyInput(value);
    if (isNaN(number)) return value;
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleValorChange = (e) => {
    const rawValue = e.target.value;
    // Allow only numbers, dots, commas
    const cleaned = rawValue.replace(/[^\d.,]/g, '');
    setValorPrecatorio(cleaned);
  };

  const handleCalculate = () => {
    setCalculationError(null);
    setResults(null);
    setBreakdown([]);

    // Validate inputs
    const startYear = parseInt(anoInicial);
    const endYear = parseInt(anoFinal);
    const initialValue = parseCurrencyInput(valorPrecatorio);
    const irrf = parseFloat(irrfPercent.replace(',', '.')) || 27.5;

    if (!anoInicial || isNaN(startYear)) {
      setCalculationError('Por favor, informe o ano inicial');
      return;
    }

    if (!anoFinal || isNaN(endYear)) {
      setCalculationError('Por favor, informe o ano final');
      return;
    }

    if (startYear > endYear) {
      setCalculationError('O ano inicial deve ser menor ou igual ao ano final');
      return;
    }

    if (!valorPrecatorio || initialValue <= 0) {
      setCalculationError('Por favor, informe o valor do precatório');
      return;
    }

    if (irrf < 0 || irrf > 100) {
      setCalculationError('O IRRF deve estar entre 0% e 100%');
      return;
    }

    // Calculate with compound interest
    let valor = initialValue;
    const breakdownData = [];

    for (let year = startYear; year <= endYear; year++) {
      const taxaEntry = taxasData.find(t => t.ano === year);
      if (!taxaEntry) {
        setCalculationError(`Taxa não encontrada para o ano ${year}`);
        return;
      }

      const valorInicial = valor;
      valor = valor * (1 + taxaEntry.taxa);

      breakdownData.push({
        ano: year,
        taxa: taxaEntry.taxa,
        valorInicial: valorInicial,
        valorFinal: valor
      });
    }

    const valorAtualizado = valor;

    // Calculate Valor Líquido with user-defined IRRF
    const valorLiquido = valorAtualizado * (1 - irrf / 100);

    // Calculate Valor de Compra Máximo
    const currentYear = new Date().getFullYear();
    const yearsUntilPayment = endYear - currentYear;
    const fixedRate = 0.06; // 6% per year
    const compoundFactor = yearsUntilPayment > 0 ? Math.pow(1 + fixedRate, yearsUntilPayment) : 1;
    const valorCompraMaximo = valorLiquido / (2 * compoundFactor);
    const percentualCompra = (valorCompraMaximo / valorLiquido) * 100;

    setResults({
      valorAtualizado,
      valorLiquido,
      valorCompraMaximo,
      percentualCompra,
      irrfUsed: irrf,
      yearsUntilPayment
    });
    setBreakdown(breakdownData);
  };

  const handleClear = () => {
    setAnoInicial('');
    setAnoFinal(new Date().getFullYear().toString());
    setValorPrecatorio('');
    setIrrfPercent('27.5');
    setResults(null);
    setBreakdown([]);
    setCalculationError(null);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>Calculadora de Precatórios</Heading>
          <Text color="gray.600">
            Calcule o valor atualizado de precatórios com base nas taxas Selic/IPCA
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Input Form */}
          <Card bg={bgCard} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Dados para Cálculo</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Ano Inicial (Orçamento)</FormLabel>
                    <NumberInput
                      value={anoInicial}
                      onChange={(valueString) => setAnoInicial(valueString)}
                      min={1900}
                      max={2100}
                    >
                      <NumberInputField placeholder="Ex: 2020" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Ano Final (Pagamento Previsto)</FormLabel>
                    <NumberInput
                      value={anoFinal}
                      onChange={(valueString) => setAnoFinal(valueString)}
                      min={1900}
                      max={2100}
                    >
                      <NumberInputField placeholder="Ex: 2025" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                  <FormLabel>Valor do Precatório</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>R$</InputLeftAddon>
                    <Input
                      value={valorPrecatorio}
                      onChange={handleValorChange}
                      placeholder="Ex: 100.000,00"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>IRRF (%)</FormLabel>
                  <InputGroup>
                    <Input
                      value={irrfPercent}
                      onChange={(e) => setIrrfPercent(e.target.value)}
                      placeholder="27.5"
                      type="text"
                    />
                    <InputLeftAddon>%</InputLeftAddon>
                  </InputGroup>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Padrão: 27,5% para natureza Comum/Alimentar
                  </Text>
                </FormControl>

                {calculationError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {calculationError}
                  </Alert>
                )}

                <HStack spacing={4} pt={2}>
                  <Button
                    colorScheme="blue"
                    leftIcon={<MdCalculate />}
                    onClick={handleCalculate}
                    flex={1}
                  >
                    Calcular
                  </Button>
                  <Button
                    variant="outline"
                    leftIcon={<MdRefresh />}
                    onClick={handleClear}
                  >
                    Limpar
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Tax Rates Table */}
          <Card bg={bgCard} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Taxas Selic/IPCA</Heading>
            </CardHeader>
            <CardBody>
              <Box maxH="350px" overflowY="auto">
                <Table size="sm" variant="striped">
                  <Thead position="sticky" top={0} bg={bgCard} zIndex={1}>
                    <Tr>
                      <Th>Ano</Th>
                      <Th isNumeric>Taxa (%)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {taxasData.map((taxa) => (
                      <Tr key={taxa._id || taxa.ano}>
                        <Td fontWeight="medium">{taxa.ano}</Td>
                        <Td isNumeric>{formatPercent(taxa.taxa)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              {taxasData.length === 0 && (
                <Text color="gray.500" textAlign="center" py={4}>
                  Nenhuma taxa cadastrada
                </Text>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Results */}
        {results && (
          <>
            <Divider />

            <Card bg={bgCard} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md">Resultado do Cálculo</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Stat>
                    <StatLabel>Valor Atualizado e Calculado</StatLabel>
                    <StatNumber color="blue.500">
                      {formatCurrency(results.valorAtualizado)}
                    </StatNumber>
                    <StatHelpText>
                      Com correção Selic/IPCA
                    </StatHelpText>
                  </Stat>

                  <Stat>
                    <StatLabel>Valor Líquido ({results.irrfUsed}% IRRF)</StatLabel>
                    <StatNumber color="green.500">
                      {formatCurrency(results.valorLiquido)}
                    </StatNumber>
                    <StatHelpText>
                      Após desconto IRRF
                    </StatHelpText>
                  </Stat>

                  <Stat>
                    <StatLabel>Valor de Compra Máximo</StatLabel>
                    <StatNumber color="purple.500">
                      {formatCurrency(results.valorCompraMaximo)}
                    </StatNumber>
                    <StatHelpText>
                      <Badge colorScheme="purple">
                        {results.percentualCompra.toFixed(1)}% do valor líquido
                      </Badge>
                      {results.yearsUntilPayment > 0 && (
                        <Text fontSize="xs" mt={1}>
                          {results.yearsUntilPayment} ano(s) até pagamento previsto
                        </Text>
                      )}
                    </StatHelpText>
                  </Stat>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Year-by-Year Breakdown */}
            {breakdown.length > 0 && (
              <Card bg={bgCard} borderColor={borderColor} borderWidth="1px">
                <CardHeader>
                  <Heading size="md">Detalhamento Ano a Ano</Heading>
                </CardHeader>
                <CardBody>
                  <Box overflowX="auto">
                    <Table size="sm" variant="striped">
                      <Thead>
                        <Tr>
                          <Th>Ano</Th>
                          <Th isNumeric>Taxa (%)</Th>
                          <Th isNumeric>Valor Inicial</Th>
                          <Th isNumeric>Valor Final</Th>
                          <Th isNumeric>Acréscimo</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {breakdown.map((item, index) => (
                          <Tr key={item.ano}>
                            <Td fontWeight="medium">{item.ano}</Td>
                            <Td isNumeric>{formatPercent(item.taxa)}</Td>
                            <Td isNumeric>{formatCurrency(item.valorInicial)}</Td>
                            <Td isNumeric fontWeight="medium">{formatCurrency(item.valorFinal)}</Td>
                            <Td isNumeric color="green.500">
                              +{formatCurrency(item.valorFinal - item.valorInicial)}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </CardBody>
              </Card>
            )}
          </>
        )}
      </VStack>
    </Container>
  );
};

export default Calculator;
